import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import PageLayout from '@/components/feature/PageLayout';
import SeoHead from '@/components/feature/SeoHead';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import RelationBoard from '@/components/base/RelationBoard';
import SectionHeading from '@/components/base/SectionHeading';
import { cases } from '@/mocks/cases';
import { persons } from '@/mocks/persons';
import { siteMeta } from '@/mocks/site';
import { getPerson } from '@/utils/content';
import type { CaseItem, Person } from '@/types/content';

const caseList = cases as unknown as CaseItem[];
const personList = persons as unknown as Person[];

function busiestPersonId(item: CaseItem): string {
  return [...item.personIds].sort((a, b) => {
    const countA = item.relations.filter((r) => r.from === a || r.to === a).length;
    const countB = item.relations.filter((r) => r.from === b || r.to === b).length;
    return countB - countA;
  })[0];
}

export default function ChartsPage() {
  const [caseIndex, setCaseIndex] = useState(0);
  const activeCase = caseList[caseIndex];
  const [centerId, setCenterId] = useState(() => busiestPersonId(caseList[0]));

  const center = getPerson(centerId);

  const selectCase = (index: number) => {
    setCaseIndex(index);
    setCenterId(busiestPersonId(caseList[index]));
  };

  const jsonLd = useMemo(
    () => [
      {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: '人物关系图总览',
        url: `${siteMeta.domain}/charts`,
        description: '按事件切换查看的人物关系网络，标注每一条关系类型与合作往来。',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: siteMeta.domain },
          { '@type': 'ListItem', position: 2, name: '人物关系图', item: `${siteMeta.domain}/charts` },
        ],
      },
    ],
    []
  );

  return (
    <PageLayout>
      <SeoHead
        title="人物关系图 | 关系网络总览 · 911吃瓜"
        description="911吃瓜人物关系图总览，按事件切换查看当事人之间的关系网络，每一条连线都标注关系类型，点击人物即可切换中心视角，快速理清圈内关系。"
        keywords="人物关系图,关系网络,明星关系梳理,关系速查,911吃瓜"
        path="/charts"
        jsonLd={jsonLd}
      />

      <section className="w-full bg-background-100 border-b border-background-200 pt-24 md:pt-28 pb-10">
        <div className="w-full px-4 md:px-8">
          <Breadcrumbs items={[{ label: '人物关系图' }]} />
          <h1 className="mt-4 font-heading font-black text-3xl md:text-4xl text-foreground-950">人物关系图</h1>
          <p className="mt-3 max-w-3xl text-sm md:text-base leading-7 text-foreground-600">
            关系图把当事人之间的交集画在一张网上：谁是起点、谁在中间牵线、谁只是同场关联，都能顺着连线读出来。选择一个事件开始浏览。
          </p>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 py-8 md:py-12">
        <div className="flex flex-wrap gap-2">
          {caseList.map((item, index) => (
            <button
              key={item.id}
              type="button"
              onClick={() => selectCase(index)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer ${
                caseIndex === index
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-background-100 text-foreground-700 hover:bg-background-200'
              }`}
            >
              {item.title}
            </button>
          ))}
        </div>

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-8">
            <RelationBoard centerId={centerId} relations={activeCase.relations} onSelect={(pid) => setCenterId(pid)} />
          </div>

          <div className="lg:col-span-4 space-y-5">
            <div className="bg-background-100 border border-background-200 rounded-lg p-5">
              <p className="text-xs font-semibold tracking-[0.18em] text-primary-600 uppercase">当前中心</p>
              {center && (
                <div className="mt-4 flex items-center gap-3">
                  <img
                    src={center.avatar}
                    alt={`${center.name} 头像`}
                    title={`${center.name} 关系中心 - 911吃瓜`}
                    className="w-14 h-14 rounded-full object-cover object-top"
                  />
                  <div>
                    <p className="font-heading font-bold text-foreground-950">{center.name}</p>
                    <p className="text-xs text-foreground-500">{center.role}</p>
                  </div>
                </div>
              )}
              <p className="mt-4 text-sm leading-7 text-foreground-600">{center?.note}</p>
            </div>

            <div className="bg-background-100 border border-background-200 rounded-lg p-5">
              <p className="text-sm font-semibold text-foreground-900 mb-3">关系条目</p>
              <ul className="space-y-2.5">
                {activeCase.relations.map((rel, index) => (
                  <li key={index} className="text-sm text-foreground-700 flex items-center gap-2 flex-wrap">
                    <span className="font-medium whitespace-nowrap">{getPerson(rel.from)?.name}</span>
                    <span className="px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-900 text-[11px] whitespace-nowrap">
                      {rel.label}
                    </span>
                    <span className="font-medium whitespace-nowrap">{getPerson(rel.to)?.name}</span>
                  </li>
                ))}
              </ul>
              <Link
                to={`/case/${activeCase.id}`}
                className="mt-5 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                查看该事件完整梳理
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="w-full bg-background-100 border-y border-background-200">
        <div className="w-full px-4 md:px-8 py-14 md:py-20">
          <SectionHeading
            eyebrow="人物档案"
            title="认识一下出场的人物"
            desc="每位人物都附有身份与相关背景说明，看新话题之前先熟悉一下他们，会更容易对上号。"
          />
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {personList.map((person) => {
              const relatedCount = caseList.filter((c) => c.personIds.includes(person.id)).length;
              return (
                <article key={person.id} className="bg-background-50 border border-background-200 rounded-lg p-5 flex gap-4">
                  <img
                    src={person.avatar}
                    alt={`${person.name} 头像`}
                    title={`${person.name} - 人物档案`}
                    className="w-16 h-16 rounded-full object-cover object-top shrink-0"
                  />
                  <div className="min-w-0">
                    <h3 className="font-heading font-bold text-foreground-950">{person.name}</h3>
                    <p className="text-xs text-primary-600 font-medium mt-0.5">{person.role}</p>
                    <p className="mt-2 text-sm leading-6 text-foreground-600">{person.note}</p>
                    <p className="mt-2 text-xs text-foreground-500">关联 {relatedCount} 个事件</p>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>
    </PageLayout>
  );
}